import React, { useEffect, useRef, useCallback } from 'react';
import './StandardModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

const SIZE_CLASS = {
  small: 'rs-std-modal--small',
  medium: 'rs-std-modal--medium',
  large: 'rs-std-modal--large',
  'extra-large': 'rs-std-modal--xl'
};

export default function StandardModal({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  showCloseButton = true
}) {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Trap focus inside modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusable = modal.querySelectorAll(
      'button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [onClose]);

  // Save + restore focus, lock body scroll
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';

    // Focus the modal itself on open
    setTimeout(() => {
      const modal = modalRef.current;
      if (modal) {
        const firstFocusable = modal.querySelector('button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
        if (firstFocusable) firstFocusable.focus();
      }
    }, 50);

    return () => {
      document.body.style.overflow = '';
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="rs-modal-overlay" onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <div
        ref={modalRef}
        className={`rs-modal ${SIZE_CLASS[size] || SIZE_CLASS.medium}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="std-modal-title"
      >
        <div className="rs-modal-header">
          <h2 id="std-modal-title">{title}</h2>
          {showCloseButton && (
            <button className="rs-modal-close" onClick={onClose} type="button" aria-label="Close modal">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
        <div className="rs-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}
